
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useInviteLink = (tripId: string) => {
  const [isSharing, setIsSharing] = useState(false);

  /**
   * Generates a shareable link for the trip
   */
  const generateShareLink = async (): Promise<string | null> => {
    try {
      setIsSharing(true);
      
      // Get the trip's invite code
      const { data: tripData, error } = await supabase
        .from('trips')
        .select('invite_code')
        .eq('id', tripId)
        .single();
      
      if (error) {
        console.error('Error fetching trip invite code:', error);
        toast.error('Could not generate invite link');
        return null;
      }
      
      if (!tripData || !tripData.invite_code) {
        console.error('Missing invite code for trip:', tripId);
        toast.error('Trip is missing an invitation code');
        
        // Try to generate and save a new invite code as a fallback
        const newInviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();
        
        const { error: updateError } = await supabase
          .from('trips')
          .update({ invite_code: newInviteCode })
          .eq('id', tripId);
          
        if (updateError) {
          console.error('Error updating invite code:', updateError);
          return null;
        }
        
        // Construct the invitation URL with the new code
        const baseUrl = 'https://wetrippy.co';
        const inviteUrl = `${baseUrl}/join?code=${newInviteCode}`;
        return inviteUrl;
      }
      
      // Construct the invitation URL with wetrippy.co domain
      const baseUrl = 'https://wetrippy.co';
      const inviteUrl = `${baseUrl}/join?code=${tripData.invite_code}`;
      
      return inviteUrl;
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate sharing link');
      return null;
    } finally {
      setIsSharing(false);
    }
  };

  /**
   * Copies the invitation link to clipboard
   */
  const copyLinkToClipboard = async () => {
    const link = await generateShareLink();
    
    if (!link) return;
    
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invitation link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Could not copy link to clipboard');
      
      // Fallback: Show the link to the user
      toast.info('Share this link: ' + link);
    }
  };

  return {
    generateShareLink,
    copyLinkToClipboard,
    isSharing
  };
};
